import { useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import {
  CareCategories,
  ChipCategories,
  Constants,
  CoverageTypesCodes,
  ProviderTypes,
  ReverseCoverageTypesCodes,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { buildBhGroupedAoeCategoriesCode } from '../../common/ContentFilters/AllFilterDrawer/ProviderFilters/GroupedAoeFilter/groupedAoe.utils';
import {
  isEniSsoMember,
  isUmrMember,
} from '../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../context/PortalContext';
import { isMissingParameters } from '../../errors/helpers';
import { useConfigs } from '../../frontends/ProviderSearch/context/Analytics/Analytics';
import {
  getBehavioralProgramId,
  getUpdatedAoeCodesWithEAP,
  getUpdatedUesAoeCodes,
  getUpdateduesBehavioralFacilityProgram,
  isHavingMedicalAoeCodeByCoverageType,
} from '../../frontends/ProviderSearch/routes/SearchResults/utils';
import { CustomPreferredPolicy } from '../../models/Lagoon';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { useSearchStore } from '../../store/useSearchStore';
import { SearchState } from '../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { ternaryExpression } from '../../utils/common';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { getMapViewPageSize } from '../../utils/map.utils';
import {
  getAutoIncrementRadius,
  getBehavioralFacilityProgramsProviderSearch,
  getBehavioralFacilityProgramsProviderSearchV2,
  getExcludeNatAncForNetworkIds,
  getIncludeSpecialityRollupCodes,
  getLocale,
  getMemberHealthCoverageType,
  getPlanVariationCodeForSearchParams,
  getProviderAoeCodes,
  getProviderAoeCodesV2,
  getProviderIds,
  getRegionalVirtualCareProviderArray,
  getRollupCodeMap,
  getSortByValue,
  getUrlParams,
  getVirtualCareLicenseStateCodes,
  isValidPlanVariationCode,
  memberCatRPK,
  shouldBlockNationalProviders,
  shouldEnableRegionalVirtualCareSection,
} from '../../utils/providerSearch.utils';
import {
  resolveIsPCPLandingPage,
  resolvePcpIndicator,
} from '../../utils/typeahead.utils';
import {
  filterCoverageTypesForStandaloneDental,
  getClaimOrEligibilitySystemTypeCode,
  getCoverageEffDateRange,
  getCoverageTypes,
  getIsCustomPreferredPolicy,
  getIsPolicyIDRequired,
  getMemberPlanYear,
  getNetworkIdsForPES,
  getOnlineRetailers,
  getPlanVariationCode,
  getSitusState,
  isPlanVariationCodeRequired,
  useIsEligibleForStandaloneDental,
} from '../../utils/user.utils';
import { getCategoryLLMFlag } from '../useCategoryLLMFlag/useCategoryLLMFlag';
import { useCoverageType } from '../useCoverageType';
import { useCurrentMemberOrDependentInfo } from '../useCurrentMember';
import { useCurrentMemberIsPreEffective } from '../useCurrentMemberIsPreEffective';
import { useFeatureFlag } from '../useFeatureFlag';
import { useGeoLocationStorage } from '../useGeoLocationStorage';
import { useLagoon } from '../useLagoon';
import { useSmartChoice } from '../useSmartChoice';
import { useProviderSearchV2 } from './useProviderSearch';

export interface ProviderSearchParams {
  agesAoeCodes: string[];
  pcpID?: string;
  pcpIndicator: boolean | null;
  acceptingNewPatients?: boolean;
  providerType: string;
  providerQueryTypes: any[];
  searchType: string;
  coverageType: string;
  virtualCare: boolean;
  searchRadius: number;
  isExtendedSearchEnabled: boolean;
  preSelectEapFilter: boolean;
  conditionAoeCodes: string[];
  aoeCodes?: string[];
  includeSpecialityRollupCodes: string[];
  behavioralFacilityPrograms: string;
  eligibilitySystemTypeCode: string;
  claimSystemTypeCode: string;
  planVariationCode: string;
  typeaheadAoeCodes: string[];
  preferredFacilities: string[];
  isUserTextSearch: boolean;
  keyword: string;
  shouldGetHGData: boolean;
  autoIncrementRadius: string[];
  behavioralProgramId: string;
  includeAdditionalFieldsForPrint: boolean;
  mapLocationsOnly: boolean;
  extendedPinsDrawerReq: boolean;
  providerLocationId: string;
  search: string;
  uniqueSearch: string;
  typeaheadSearch: boolean;
  gender: string;
  phoneNumber?: string;
  aoeCategoriesCode?: string;
  isStaffModel?: boolean;
  networkTypeCode?: string;
}

export const useProviderResultsV2 = (customParams: any, options = {}) => {
  const [queryParams, setQueryParams] = useState({});
  const [providerResults, getProviders] = useProviderSearchV2(options);

  const {
    uesAoeCodefromUES,
    uesBehavioralFacilityProgramfromUES,
    bhProgramFacilityAgg,
    preferredSpecialityCode,
    platinumProgramCode,
  } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      uesAoeCodefromUES: state.typeaheadSearchStore.aoeCodes,
      uesBehavioralFacilityProgramfromUES:
        state.typeaheadSearchStore?.bhProgramFacility,
      bhProgramFacilityAgg: state.typeaheadSearchStore.bhProgramFacilityAgg,
      preferredSpecialityCode:
        state.typeaheadSearchStore.preferredSpecialityCode,
      platinumProgramCode: state.typeaheadSearchStore.platinumProgramCode,
    }))
  );

  const { storeLob, chipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      storeLob: state.lob,
      chipValue: state.chipValue,
    }))
  );

  const { dependentSeqNbr, choosePCP } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
      choosePCP: state.pcpSearchState.choosePCP,
    }))
  );

  const { onlineRetailers } = useSearchStore(
    useShallow((state: SearchState) => ({
      onlineRetailers: state.searchStore.onlineRetailers,
    }))
  );

  const coverageTypeFromStore = useCoverageType();
  const currentMember = useCurrentMemberOrDependentInfo(dependentSeqNbr);
  const isEligibleForStandaloneDental = useIsEligibleForStandaloneDental();
  const { portalData } = useContext(PortalContext);
  const { longitude, latitude, stateCode } = useGeoLocationStorage();
  const isSmartChoice = useSmartChoice();
  const isPreEffective = useCurrentMemberIsPreEffective();

  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const customPreferredPolicies: CustomPreferredPolicy[] = useLagoon(
    'custom-preferred-policies'
  )();
  const rollupCodeMapLagoonData = useLagoon(
    Constants.LAGOON_TABLE.ROLLUPCODE_MAPPING
  )();
  const tierBenefits = useLagoon('tier-benefit')();
  const allConfigs = useLagoon('config')();

  const [
    useMedicalNewRollUpCodes,
    useBHNewRollUpCodes,
    useVisionNewRollUpCodes,
    useDentalNewRollUpCodes,
    uspTierOneFlag,
    uspToggleFlag,
    isFutureTerminationDemo,
    enableMockProviderIdsForPreEffective,
    enableTierOneMockProviderIds,
    disablePCPIndAndANPFlag,
    disablePCPIndAndANPEarlyPayFlag,
    eapBHProgramFlag,
    enableMapViewLargeResults,
    enableVirtualCareEpic,
    enableClaimsBasedLlmAllLanguages,
    enableCategoryCountV2,
    enableBhGroupedAoeFilter,
    enableAllStaffModelEni,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.USE_MEDICAL_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_BH_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_VISION_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_DENTAL_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USP_TIER1_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.USP_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.FUTURE_TERMINATION_DEMO,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MOCK_PROVIDER_IDS_FOR_PRE_EFFECTIVE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER_ONE_MOCK_PROVIDER_IDS,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PCP_IND_AND_ANP,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PCP_IND_AND_ANP_EARLYPAY,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_EAP_FILTER_USING_BH_PROGRAM,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_LARGE_RESULTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CLAIMS_BASED_LLM_ALL_LANGUAGES,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CATEGORY_COUNT_V2,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_GROUPED_AOE_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_STAFF_MODEL_ENI,
  ]);

  const [
    mapViewPageSizeConfig,
    terminationProviderIdsDemo,
    futureStartDateProviderIds,
    tierOneMockProviderIds,
    acoMockProviderIds,
    premiumCareMockProviderIds,
    mnrFutureMockProviderIds,
    excludeNatAncForNetworkIds,
    autoIncrementRadius,
  ] = useConfigs([
    ConstantsLagoon.CONFIG.MAP_VIEW_PAGE_SIZE,
    'TERMINATION_PROVIDER_IDS_DEMO',
    'FUTURE_START_PROVIDERIDS',
    'TIER1_MOCK_PROVIDERIDS',
    'ACO_MOCK_PROVIDERIDS',
    'PREMIUM_MOCK_PROVIDERIDS',
    'MNR_MOCK_PROVIDERIDS',
    'EXCLUDE_NAT_ANC_FOR_NETWORK_IDS',
    'AUTO_INCREMENT_SEARCH_RADIUS',
  ]);
  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const {
    chipValue: chipValueFromParams,
    isDeepLink,
    coverageType: coverageTypeFromParams,
    searchSuggestionEN,
    [Constants.PSX_SOURCE]: psxSource,
    [Constants.SOURCE]: sourceFromGlobalSearch,
  } = getUrlParams();
  const searchSuggestionENValue = enableClaimsBasedLlmAllLanguages
    ? searchSuggestionEN
    : '';
  const coverageType = coverageTypeFromParams || coverageTypeFromStore;

  const allCoverages = getCoverageTypes(currentMember);
  const coverages = isEligibleForStandaloneDental
    ? filterCoverageTypesForStandaloneDental(allCoverages)
    : allCoverages;
  const regionalProviderArray = getRegionalVirtualCareProviderArray(
    chipValue,
    allConfigs
  );

  const virtualCareLicenseStateCode = getVirtualCareLicenseStateCodes(
    regionalProviderArray
  );
  const enableRegionalVirtualCareSection =
    shouldEnableRegionalVirtualCareSection(
      enableVirtualCareEpic,
      isVirtualCareUnetRailroadSuppressed,
      regionalProviderArray
    );

  const medicalCoverage = getMemberHealthCoverageType(
    currentMember?.eligibility,
    CoverageTypesCodes.MEDICAL
  );
  const { productCode, marketType } = medicalCoverage || {};

  const {
    ddpCode,
    demographics: { gender, dateOfBirth } = {},
    isIFPEarlyPay,
    lineOfBusiness: lob = storeLob,
    population,
    policyNumber: policyID,
  } = currentMember || {};

  const autoIncrementRadiusArr = getAutoIncrementRadius(autoIncrementRadius);
  const rollupCodeMap = getRollupCodeMap(rollupCodeMapLagoonData);

  const reciprocityId = getNetworkIdsForPES(
    currentMember,
    coverageType,
    featureFlags
  );

  const situsState = getSitusState(currentMember, lob);

  const isCustomPreferredPolicy: boolean = getIsCustomPreferredPolicy(
    customPreferredPolicies,
    policyID,
    reciprocityId,
    situsState
  );

  const currentMemberEffectiveDate = getCoverageEffDateRange(
    currentMember,
    coverageType
  ).startDate;

  const isPCPLandingPage = resolveIsPCPLandingPage(
    chipValue,
    psxSource,
    sourceFromGlobalSearch
  );

  const disablePCPIndAndANP = currentMember?.isIFPEarlyPay
    ? disablePCPIndAndANPEarlyPayFlag
    : disablePCPIndAndANPFlag;

  const [, setCurrentTypeaheadParamsPractitioner] = useSessionStorage<any>(
    `${Constants.STORAGE_KEYS.SESSION.MIXED_RESULTS_TYPE_AHEAD_PARAMS}_${ProviderTypes.PRACTITIONER}`,
    {}
  );
  const [, setCurrentTypeaheadParamsMedical] = useSessionStorage<any>(
    `${Constants.STORAGE_KEYS.SESSION.MIXED_RESULTS_TYPE_AHEAD_PARAMS}_${ProviderTypes.MEDICAL_GROUP}`,
    {}
  );
  const [, setCurrentTypeaheadParamsOrg] = useSessionStorage<any>(
    `${Constants.STORAGE_KEYS.SESSION.MIXED_RESULTS_TYPE_AHEAD_PARAMS}_${ProviderTypes.ORGANIZATION}`,
    {}
  );
  const [searchParamsList] = useSearchParams();

  const planYear = getMemberPlanYear(
    isPreEffective,
    currentMemberEffectiveDate
  );
  const pageSize = getMapViewPageSize(
    enableMapViewLargeResults,
    mapViewPageSizeConfig
  );
  const memberEffectiveDate = isPreEffective
    ? currentMemberEffectiveDate
    : null;
  const claimSystemTypeCode = getClaimOrEligibilitySystemTypeCode(
    currentMember,
    coverageType
  )?.claimSystemTypeCode;
  const eligibilitySystemTypeCode = getClaimOrEligibilitySystemTypeCode(
    currentMember,
    coverageType
  )?.eligibilitySystemTypeCode;
  const snpType =
    coverageType == ReverseCoverageTypesCodes.MEDICAL
      ? medicalCoverage?.snpType
      : '';
  const planVariationCodeNew = getPlanVariationCode(
    currentMember,
    coverageType
  );
  const planVariationCode = getPlanVariationCodeForSearchParams(
    getPlanVariationCode(currentMember, coverageType),
    planVariationCodeNew
  );
  const isUmrPopulation = isUmrMember(portalData?.portalName);

  const isGuestUser = portalData?.portalConfig?.isGuestUser;
  const isEniSsoMemberPopulation = isEniSsoMember(portalData?.portalName);
  const validPlanVariationCode = isValidPlanVariationCode(
    eligibilitySystemTypeCode,
    claimSystemTypeCode,
    planVariationCode,
    lob,
    population,
    isUmrPopulation,
    isIFPEarlyPay,
    isGuestUser,
    isEniSsoMemberPopulation,
    isEligibleForStandaloneDental
  );
  const baseVariables = {
    lob,
    population,
    coverages,
    situsState,
    pageSize,
    planYear,
    preferredSpecialityCode,
    planVariationCode,
    memberEffectiveDate,
    claimSystemTypeCode,
    eligibilitySystemTypeCode,
    policyID,
    rollupCodeMap,
    snpType,
    locale: getLocale(),
    latitude,
    longitude,
    stateCode,
    memberDemographics: { gender, dateOfBirth },
    isFutureTerminationDemo,
    terminationProviderIdsDemo,
    useMedicalNewRollUpCodes,
    useBHNewRollUpCodes,
    useVisionNewRollUpCodes,
    useDentalNewRollUpCodes,
    ddpCode,
    coverageType: coverageTypeFromParams,
    isCustomPreferredPolicy,
    cesProductCode: productCode,
    memberMarketType: marketType,
    enableMockProviderIdsForPreEffective,
    isIFPEarlyPay,
    isGuestUser,
    useNewAgeAndGenderRollups: isGuestUser ? false : disablePCPIndAndANP,
    enableTierOneMockProviderIds: enableTierOneMockProviderIds || false,
    futureStartDateProviderIds: getProviderIds(futureStartDateProviderIds),
    tierOneMockProviderIds: getProviderIds(tierOneMockProviderIds),
    acoMockProviderIds: getProviderIds(acoMockProviderIds),
    premiumCareMockProviderIds: getProviderIds(premiumCareMockProviderIds),
    mnrFutureMockProviderIds: getProviderIds(mnrFutureMockProviderIds),
    excludeNatAncForNetworkIds: getExcludeNatAncForNetworkIds(
      excludeNatAncForNetworkIds
    ),
    onlineRetailers: getOnlineRetailers(onlineRetailers),
    reciprocityId: getNetworkIdsForPES(
      currentMember,
      coverageTypeFromParams,
      featureFlags
    ),
    ...(isDeepLink
      ? {
          autoIncrementRadius,
        }
      : {}),
  };

  const getProviderResults = (params: ProviderSearchParams) => {
    const {
      search,
      uniqueSearch,
      searchType,
      providerQueryTypes,
      includeSpecialityRollupCodes: includeSpecialityRollupCodesFromParams,
      isUserTextSearch,
      keyword,
      shouldGetHGData,
      autoIncrementRadius,
      providerType,
      gender,
      typeaheadSearch,
      virtualCare,
      searchRadius,
      pcpID,
      preferredFacilities,
      phoneNumber,
      isStaffModel,
      networkTypeCode,
    } = params;

    const invokeLlmModel = ternaryExpression(
      searchParamsList.get('fromLandingPage') === 'true',
      false,
      getCategoryLLMFlag(featureFlags, providerType, chipValueFromParams || '')
    );

    const { membershipCategory, rulesPackageKey } = memberCatRPK(
      currentMember,
      uspTierOneFlag,
      uspToggleFlag,
      tierBenefits,
      providerType
    );
    const isPractitioner =
      providerType?.toLowerCase() == ProviderTypes.PRACTITIONER;
    const sortBy = getSortByValue(isPractitioner && isSmartChoice);

    const pcpIndicator = resolvePcpIndicator(
      disablePCPIndAndANP,
      isPCPLandingPage,
      choosePCP
    );

    const searchParamsObject = Object.fromEntries(searchParamsList.entries());
    const pcpIndicatorFromUrl = searchParamsObject?.choosePCP === 'true';
    const acceptingNewPatients = pcpIndicatorFromUrl ? true : undefined;

    const paramgetProviderAoeCodes = {
      providerQueryTypes,
      providerType,
      searchType,
      typeaheadAoeCodes: params.typeaheadAoeCodes,
    };

    const typeaheadAoeCodes = enableCategoryCountV2
      ? getProviderAoeCodesV2(paramgetProviderAoeCodes)
      : getProviderAoeCodes(paramgetProviderAoeCodes);

    isHavingMedicalAoeCodeByCoverageType(
      { coverageType, aoeCodes: params.aoeCodes },
      typeaheadAoeCodes
    );

    const aoeCodes = getUpdatedAoeCodesWithEAP(
      eapBHProgramFlag,
      params.preSelectEapFilter,
      params.aoeCodes || params.typeaheadAoeCodes,
      params.conditionAoeCodes,
      params.providerType
    );

    const areaOfExpertise = params.aoeCodes || [];
    const isBhGroupedAoeCoverage =
      coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
      coverageType === ReverseCoverageTypesCodes['BEHAVIORAL CARVE-OUT'];
    const aoeCategoriesCode =
      enableBhGroupedAoeFilter &&
      isBhGroupedAoeCoverage &&
      areaOfExpertise?.length
        ? buildBhGroupedAoeCategoriesCode(
            areaOfExpertise.map((value) => String(value)),
            allConfigs
          )
        : '';

    const agesAoeCodes = params.agesAoeCodes || [];

    const behavioralProgramId = getBehavioralProgramId(
      eapBHProgramFlag,
      params.preSelectEapFilter,
      params.providerType
    );

    const behavioralFacilityPrograms = enableCategoryCountV2
      ? getBehavioralFacilityProgramsProviderSearchV2({
          searchType,
          providerType,
          bhProgramFacilityAgg,
          providerQueryTypes,
        })
      : getBehavioralFacilityProgramsProviderSearch({
          searchType,
          providerType,
          bhProgramFacilityAgg,
          providerQueryTypes,
        });

    const includeSpecialityRollupCodes = getIncludeSpecialityRollupCodes(
      {
        behavioralFacilityPrograms,
        searchType,
      },
      includeSpecialityRollupCodesFromParams,
      typeaheadAoeCodes,
      enableCategoryCountV2
    );
    const chipCategory =
      (chipValueFromParams || chipValue) === CareCategories.PRIMARY_CARE
        ? ChipCategories.PRIMARY_CARE
        : '';
    const customVariables = {
      agesAoeCodes,
      pcpID,
      pcpIndicator,
      acceptingNewPatients,
      invokeLlmModel,
      membershipCategory,
      rulesPackageKey,
      sortBy,
      behavioralFacilityPrograms: enableCategoryCountV2
        ? ''
        : behavioralFacilityPrograms,
      includeSpecialityRollupCodes,
      aoeCodes: enableCategoryCountV2 || aoeCategoriesCode ? [] : aoeCodes,
      ...(aoeCategoriesCode && { aoeCategoriesCode }),
      behavioralProgramId,
      isUserTextSearch,
      searchType,
      keyword: searchSuggestionENValue || keyword,
      shouldGetHGData,
      isPCPLandingPage,
      chipCategory,
      autoIncrementRadius,
      virtualCare,
      isExtendedSearchEnabled: !pcpID,
      providerType,
      typeaheadSearch,
      search: searchSuggestionENValue || search,
      uniqueSearch,
      gender,
      preferredFacilities,
      phoneNumber,
      searchRadius: virtualCare ? -1 : searchRadius,
      ...(enableAllStaffModelEni && currentMember?.isUspNiceMember && isStaffModel && { networkAffiliationId: [networkTypeCode] }),
      ...(platinumProgramCode && { platinumProgramCode }),
      ...(isSmartChoice &&
        isPractitioner && {
          isProviderRecommendationsEnabled: isSmartChoice,
          recommendationPreferences: {
            ...customParams?.recommendationPreferences,
            recommendationRadius: virtualCare
              ? Number(
                  autoIncrementRadiusArr?.[autoIncrementRadiusArr?.length - 1]
                )
              : searchRadius,
          },
        }),
    };
    const uesAoeCodes = getUpdatedUesAoeCodes(
      uesAoeCodefromUES,
      params.providerType
    );

    const uesBehavioralFacilityProgram = getUpdateduesBehavioralFacilityProgram(
      uesBehavioralFacilityProgramfromUES,
      params.providerType
    );
    const variables = {
      ...baseVariables,
      minimumProvidersReturn: customParams?.minimumProvidersReturn,
      ...(enableRegionalVirtualCareSection &&
        !pcpID && {
          virtualOnlyCare: regionalProviderArray,
          virtualCareLicenseStateCode,
        }),
      ...(enableCategoryCountV2 && {
        uesAoeCode: uesAoeCodes,
        uesBehavioralFacilityProgram,
      }),
      shouldBlockNationalProviders: shouldBlockNationalProviders(
        chipValue,
        enableVirtualCareEpic,
        isVirtualCareUnetRailroadSuppressed,
        allConfigs
      ),
      ...customVariables,
    };

    if (JSON.stringify(queryParams) !== JSON.stringify(variables)) {
      setQueryParams(variables);
      if (!validPlanVariationCode) {
        throw new Error('Plan Variation Code is required');
      }

      switch (providerType) {
        case ProviderTypes.MEDICAL_GROUP:
          setCurrentTypeaheadParamsMedical(variables);
          break;
        case ProviderTypes.ORGANIZATION:
          setCurrentTypeaheadParamsOrg(variables);
          break;
        default:
          setCurrentTypeaheadParamsPractitioner(variables);
          break;
      }

      const planVariationCodeRequired = isPlanVariationCodeRequired(
        currentMember?.lineOfBusiness,
        currentMember?.population,
        portalData?.portalName,
        isIFPEarlyPay,
        isGuestUser,
        isEligibleForStandaloneDental
      );

      const isPolicyIDRequired = getIsPolicyIDRequired(
        currentMember,
        isGuestUser
      );
      const requiredParams = {
        coverages,
        reciprocityId,
        ...(isPolicyIDRequired ? { policyID } : {}),
        latitude,
        longitude,
        coverageType,
        ...(planVariationCodeRequired ? { planVariationCode } : {}),
      };

      if (isMissingParameters('Provider Search', requiredParams)) {
        return;
      }

      getProviders({ variables });
    }
  };

  return [providerResults, getProviderResults];
};
